import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, X, Copy, Check, Webhook, UserPlus, Loader2, RefreshCw, MessageSquare, Pencil } from "lucide-react";
import { toast, Toaster } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AdminWebhookDrawer from "../components/AdminWebhookDrawer";
import EditTenantDialog from "../components/EditTenantDialog";

export default function Admin() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [editing, setEditing] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [createdResult, setCreatedResult] = useState(null);

    const refresh = async () => {
        try {
            const [s, t] = await Promise.all([api.get("/admin/stats"), api.get("/admin/tenants")]);
            setStats(s.data);
            setTenants(t.data.tenants || []);
        } catch (_err) {
            /* ignore */
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            await refresh();
            if (mounted) setLoading(false);
        })();
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <section className="mx-auto w-full max-w-5xl px-5 sm:px-8 pt-6 sm:pt-16 pb-40 md:pb-16" data-testid="admin-page">
            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-950/60 text-[11.5px] tracking-tight text-zinc-400"
            >
                <ShieldCheck className="h-3 w-3" />
                WebJesters Admin
            </motion.div>

            <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="mt-6 text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-50"
            >
                Tenant overview
            </motion.h1>

            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-zinc-400">
                Signed in as <span className="text-zinc-200">{user?.email}</span>. Click a tenant to see their integration details.
            </p>

            {/* Create new client */}
            <div className="mt-8">
                <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    data-testid="admin-create-client-button"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-zinc-50 text-zinc-950 text-[13.5px] font-medium hover:bg-white transition-colors"
                >
                    <UserPlus className="h-4 w-4" />
                    Create new client
                </button>
            </div>

            <div className="mt-8 sm:mt-12 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                    { label: "Tenants", value: stats?.tenants ?? "—", testid: "admin-stat-tenants" },
                    { label: "Users", value: stats?.users ?? "—", testid: "admin-stat-users" },
                    { label: "Clients", value: stats?.clients ?? "—", testid: "admin-stat-clients" },
                ].map((s) => (
                    <div
                        key={s.label}
                        className="rounded-xl border border-zinc-800/80 bg-zinc-950/40 p-5"
                        data-testid={s.testid}
                    >
                        <p className="text-[11.5px] uppercase tracking-[0.14em] text-zinc-500">{s.label}</p>
                        <p className="mt-2 text-3xl font-semibold tracking-tighter text-zinc-50">{s.value}</p>
                    </div>
                ))}
            </div>

            <div className="mt-8 sm:mt-12">
                <p className="text-[11.5px] uppercase tracking-[0.14em] text-zinc-500 mb-4">Tenants</p>

                {/* Mobile: stacked cards */}
                <div className="md:hidden space-y-3" data-testid="admin-tenants-cards">
                    {loading && (
                        <p className="text-[13.5px] text-zinc-500 py-4">Loading…</p>
                    )}
                    {!loading && tenants.length === 0 && (
                        <p className="text-[13.5px] text-zinc-500 py-4">No tenants yet.</p>
                    )}
                    {tenants.map((t) => (
                        <div
                            key={t.id}
                            data-testid={`admin-tenant-card-${t.id}`}
                            className="rounded-xl border border-zinc-800/80 bg-zinc-950/40 p-4"
                        >
                            <div className="min-w-0">
                                <p className="text-[14.5px] font-medium text-zinc-50 truncate">
                                    {t.business_name}
                                </p>
                                <p className="mt-0.5 text-[12.5px] text-zinc-400 truncate">
                                    {t.owner_email}
                                </p>
                                <p className="mt-0.5 text-[11.5px] text-zinc-600">
                                    {t.created_at ? `Joined ${new Date(t.created_at).toLocaleDateString()}` : ""}
                                </p>
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setEditing(t)}
                                    data-testid={`admin-edit-tenant-mobile-${t.id}`}
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-zinc-800 bg-zinc-950 text-[12.5px] text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 transition-colors flex-1 justify-center"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActive(t)}
                                    data-testid={`admin-view-webhook-mobile-${t.id}`}
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-zinc-800 bg-zinc-950 text-[12.5px] text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 transition-colors flex-1 justify-center"
                                >
                                    <Webhook className="h-3.5 w-3.5" />
                                    Integration
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Desktop: table */}
                <div className="hidden md:block rounded-xl border border-zinc-800/80 bg-zinc-950/40 overflow-hidden">
                    <table className="w-full text-left text-[13.5px]" data-testid="admin-tenants-table">
                        <thead>
                            <tr className="text-zinc-500 border-b border-zinc-800/80">
                                <th className="font-medium py-3 px-4">Business</th>
                                <th className="font-medium py-3 px-4">Owner</th>
                                <th className="font-medium py-3 px-4">Created</th>
                                <th className="font-medium py-3 px-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={4} className="py-6 px-4 text-zinc-500">
                                        Loading…
                                    </td>
                                </tr>
                            )}
                            {!loading && tenants.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="py-6 px-4 text-zinc-500">
                                        No tenants yet.
                                    </td>
                                </tr>
                            )}
                            {tenants.map((t) => (
                                <tr
                                    key={t.id}
                                    className="border-b border-zinc-900 last:border-0 hover:bg-zinc-950/60 transition-colors"
                                    data-testid={`admin-tenant-row-${t.id}`}
                                >
                                    <td className="py-3 px-4 text-zinc-100">{t.business_name}</td>
                                    <td className="py-3 px-4 text-zinc-400">{t.owner_email}</td>
                                    <td className="py-3 px-4 text-zinc-500">
                                        {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        <div className="inline-flex items-center gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => setEditing(t)}
                                                data-testid={`admin-edit-tenant-${t.id}`}
                                                title="Edit email / business name"
                                                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-zinc-800 bg-zinc-950 text-[12px] text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                                            >
                                                <Pencil className="h-3 w-3" />
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setActive(t)}
                                                data-testid={`admin-view-webhook-${t.id}`}
                                                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-zinc-800 bg-zinc-950 text-[12px] text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                                            >
                                                <Webhook className="h-3 w-3" />
                                                Integration
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AdminStripeEventsSection />

            <AdminChangeRequestsSection />

            <AdminWebhookDrawer tenant={active} onClose={() => setActive(null)} />
            <EditTenantDialog
                tenant={editing}
                onClose={() => setEditing(null)}
                onSaved={async () => {
                    setEditing(null);
                    await refresh();
                }}
            />
            <CreateClientDialog
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={async (result) => {
                    setCreatedResult(result);
                    setShowCreate(false);
                    await refresh();
                }}
            />
            <CredentialsDialog
                result={createdResult}
                onClose={() => setCreatedResult(null)}
            />

            <Toaster
                theme="dark"
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid #27272a",
                        color: "#f4f4f5",
                    },
                }}
            />
        </section>
    );
}

function CreateClientDialog({ open, onClose, onCreated }) {
    const [businessName, setBusinessName] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) {
            setBusinessName("");
            setEmail("");
            setError("");
        }
    }, [open]);

    const canSubmit = businessName.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(email.trim());

    const submit = async () => {
        setError("");
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/tenants/provision", {
                business_name: businessName.trim(),
                email: email.trim().toLowerCase(),
            });
            await onCreated(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
                    onClick={onClose}
                    data-testid="create-client-overlay"
                >
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full sm:max-w-[440px] bg-[#0a0a0a] border border-zinc-800 rounded-t-2xl sm:rounded-xl p-6"
                        data-testid="create-client-dialog"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                                    Provision
                                </p>
                                <h2 className="mt-1 text-xl font-medium text-zinc-50 tracking-tight">
                                    Create new client
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                data-testid="create-client-close"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="mt-6 space-y-4">
                            <label className="block">
                                <span className="block text-[12.5px] text-zinc-400 font-medium mb-2">
                                    Business name
                                </span>
                                <input
                                    type="text"
                                    maxLength={120}
                                    value={businessName}
                                    onChange={(e) => setBusinessName(e.target.value)}
                                    placeholder="e.g. Riverside Roofing"
                                    data-testid="create-client-business-name"
                                    className="w-full h-11 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-500"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[12.5px] text-zinc-400 font-medium mb-2">
                                    Owner email
                                </span>
                                <input
                                    type="email"
                                    maxLength={120}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="owner@business.com"
                                    data-testid="create-client-email"
                                    className="w-full h-11 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-500"
                                />
                            </label>

                            <p className="text-[12px] text-zinc-500 leading-relaxed">
                                A temporary password and webhook key will be generated automatically. You'll see them once on the next screen — share securely with the client.
                            </p>

                            {error && (
                                <p className="text-[12.5px] text-red-400" data-testid="create-client-error">
                                    {error}
                                </p>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="create-client-cancel"
                                className="h-10 px-4 rounded-md border border-zinc-800 text-[13px] text-zinc-300 hover:bg-zinc-900 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={!canSubmit || submitting}
                                data-testid="create-client-submit"
                                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-zinc-50 text-zinc-950 text-[13.5px] font-medium hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                Provision client
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function CredentialsDialog({ result, onClose }) {
    const [copied, setCopied] = useState(null);

    const copy = async (value, key) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(key);
            toast("Copied", { duration: 1500 });
            setTimeout(() => setCopied(null), 1600);
        } catch (_e) {
            /* ignore */
        }
    };

    return (
        <AnimatePresence>
            {result && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
                    onClick={onClose}
                    data-testid="credentials-overlay"
                >
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full sm:max-w-[520px] bg-[#0a0a0a] border border-zinc-800 rounded-t-2xl sm:rounded-xl p-6"
                        data-testid="credentials-dialog"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                                    Provisioned
                                </p>
                                <h2 className="mt-1 text-xl font-medium text-zinc-50 tracking-tight truncate">
                                    {result.user.tenant.business_name}
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                data-testid="credentials-close"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <p className="mt-4 text-[13px] text-amber-300/90 border border-amber-900/40 bg-amber-950/30 rounded-md px-3 py-2">
                            One-time display. Save these now — WebJesters cannot retrieve the password later.
                        </p>

                        <div className="mt-6 space-y-4">
                            {[
                                { label: "Email", value: result.user.email, k: "email" },
                                { label: "Temporary password", value: result.initial_password, k: "password" },
                                { label: "Webhook secret", value: result.user.tenant.webhook_secret, k: "secret" },
                            ].map((row) => (
                                <div key={row.k}>
                                    <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5">
                                        {row.label}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <code
                                            className="flex-1 min-w-0 text-[12.5px] text-zinc-100 font-mono bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 truncate"
                                            data-testid={`credentials-${row.k}`}
                                        >
                                            {row.value}
                                        </code>
                                        <button
                                            type="button"
                                            onClick={() => copy(row.value, row.k)}
                                            data-testid={`credentials-copy-${row.k}`}
                                            aria-label={`Copy ${row.label}`}
                                            className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                                        >
                                            {copied === row.k ? (
                                                <Check className="h-4 w-4 text-emerald-400" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="credentials-done"
                                className="h-10 px-5 rounded-md bg-zinc-50 text-zinc-950 text-[13.5px] font-medium hover:bg-white transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}


const STATUS_OPTIONS = [
    { value: "submitted", label: "Submitted" },
    { value: "in_progress", label: "In progress" },
    { value: "completed", label: "Completed" },
];

const STATUS_STYLES = {
    submitted: "border-zinc-700 bg-zinc-900 text-zinc-300",
    in_progress: "border-amber-900/60 bg-amber-950/40 text-amber-200",
    completed: "border-emerald-900/60 bg-emerald-950/30 text-emerald-300",
};

function AdminChangeRequestsSection() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [expanded, setExpanded] = useState({});

    const load = async () => {
        try {
            const { data } = await api.get("/admin/change-requests");
            setRequests(Array.isArray(data) ? data : []);
        } catch (_e) {
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateStatus = async (id, status) => {
        setSavingId(id);
        try {
            const { data } = await api.patch(`/change-requests/${id}/status`, { status });
            setRequests((prev) =>
                prev.map((r) => (r.id === id ? { ...r, ...data } : r))
            );
            window.dispatchEvent(new Event("webjesters:requests-changed"));
            toast.success("Status updated");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
        } finally {
            setSavingId(null);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch (_e) {
            return iso;
        }
    };

    return (
        <div className="mt-16" data-testid="admin-requests-section">
            <div className="flex items-end justify-between mb-4 gap-3">
                <div>
                    <p className="text-[11.5px] uppercase tracking-[0.14em] text-zinc-500">
                        Change requests
                    </p>
                    <h2 className="mt-1 text-[20px] font-medium text-zinc-100 tracking-tight inline-flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-zinc-500" />
                        Cross-tenant queue
                    </h2>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="admin-requests-refresh"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-zinc-800 text-[12px] text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Refresh
                </button>
            </div>

            <div className="rounded-xl border border-zinc-800/80 bg-zinc-950/40 overflow-hidden">
                {loading && (
                    <p className="p-6 text-[13.5px] text-zinc-500" data-testid="admin-requests-loading">
                        Loading requests…
                    </p>
                )}
                {!loading && requests.length === 0 && (
                    <p className="p-6 text-[13.5px] text-zinc-500" data-testid="admin-requests-empty">
                        No change requests yet.
                    </p>
                )}
                {!loading && requests.length > 0 && (
                    <ul className="divide-y divide-zinc-900" data-testid="admin-requests-list">
                        {requests.map((r) => {
                            const isOpen = !!expanded[r.id];
                            const styleCls = STATUS_STYLES[r.status] || STATUS_STYLES.submitted;
                            return (
                                <li
                                    key={r.id}
                                    className="px-5 py-4"
                                    data-testid={`admin-request-row-${r.id}`}
                                >
                                    <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setExpanded((prev) => ({
                                                    ...prev,
                                                    [r.id]: !prev[r.id],
                                                }))
                                            }
                                            data-testid={`admin-request-toggle-${r.id}`}
                                            className="flex-1 min-w-0 text-left"
                                        >
                                            <p className="text-[14px] font-medium text-zinc-100 truncate">
                                                {r.title}
                                            </p>
                                            <p className="mt-0.5 text-[12.5px] text-zinc-500 truncate">
                                                {r.business_name || "Unknown tenant"}
                                                <span className="text-zinc-700 mx-1.5">·</span>
                                                {formatDate(r.created_at)}
                                                <span className="text-zinc-700 mx-1.5">·</span>
                                                {r.submitted_by}
                                            </p>
                                        </button>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <select
                                                value={r.status}
                                                onChange={(e) => updateStatus(r.id, e.target.value)}
                                                disabled={savingId === r.id}
                                                data-testid={`admin-request-status-${r.id}`}
                                                aria-label="Update request status"
                                                className={[
                                                    "h-8 pl-2.5 pr-7 rounded-md border text-[12px] outline-none transition-colors cursor-pointer disabled:opacity-50",
                                                    styleCls,
                                                ].join(" ")}
                                            >
                                                {STATUS_OPTIONS.map((opt) => (
                                                    <option
                                                        key={opt.value}
                                                        value={opt.value}
                                                        className="bg-zinc-950 text-zinc-200"
                                                    >
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    {isOpen && (
                                        <p
                                            className="mt-3 text-[13px] text-zinc-300 whitespace-pre-wrap leading-relaxed border-l border-zinc-800 pl-3"
                                            data-testid={`admin-request-description-${r.id}`}
                                        >
                                            {r.description}
                                        </p>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Admin — Stripe events audit log + manual replay.
// ---------------------------------------------------------------------------
const STRIPE_STATUS_STYLES = {
    provisioned: "border-emerald-900/60 bg-emerald-950/30 text-emerald-300",
    already_provisioned: "border-zinc-700 bg-zinc-900 text-zinc-300",
    skipped_no_email: "border-amber-900/60 bg-amber-950/30 text-amber-200",
    failed: "border-red-900/60 bg-red-950/40 text-red-300",
    received: "border-zinc-700 bg-zinc-900 text-zinc-400",
};

function AdminStripeEventsSection() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replayingId, setReplayingId] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/stripe-events");
            setEvents(Array.isArray(data) ? data : []);
        } catch (_e) {
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const replay = async (event_id) => {
        if (
            !window.confirm(
                "Replay this Stripe event? Safe to retry — already-provisioned customers will be skipped automatically."
            )
        )
            return;
        setReplayingId(event_id);
        try {
            const { data } = await api.post(`/admin/stripe-events/${event_id}/replay`);
            if (data?.provisioned) toast.success("Customer provisioned + welcome email sent.");
            else if (data?.already_provisioned) toast("Already provisioned — no action taken.");
            else toast(`Replay completed (${JSON.stringify(data)})`);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Replay failed");
        } finally {
            setReplayingId(null);
        }
    };

    const fmt = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch (_e) {
            return iso;
        }
    };

    return (
        <div className="mt-16" data-testid="admin-stripe-events-section">
            <div className="flex items-end justify-between mb-4 gap-3">
                <div>
                    <p className="text-[11.5px] uppercase tracking-[0.14em] text-zinc-500">
                        Stripe deliveries
                    </p>
                    <h2 className="mt-1 text-[20px] font-medium text-zinc-100 tracking-tight">
                        Provisioning audit
                    </h2>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="admin-stripe-refresh"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-zinc-800 text-[12px] text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Refresh
                </button>
            </div>

            <div className="rounded-xl border border-zinc-800/80 bg-zinc-950/40 overflow-hidden">
                {loading && (
                    <p className="p-6 text-[13.5px] text-zinc-500" data-testid="admin-stripe-loading">
                        Loading events…
                    </p>
                )}
                {!loading && events.length === 0 && (
                    <p className="p-6 text-[13.5px] text-zinc-500" data-testid="admin-stripe-empty">
                        No Stripe events recorded yet.
                    </p>
                )}
                {!loading && events.length > 0 && (
                    <ul className="divide-y divide-zinc-900" data-testid="admin-stripe-list">
                        {events.map((e) => {
                            const status = e.status || "received";
                            const styleCls = STRIPE_STATUS_STYLES[status] || STRIPE_STATUS_STYLES.received;
                            const isLegacy = !e.status; // pre-audit rows have no status field
                            const canReplay =
                                !isLegacy && ["received", "skipped_no_email", "failed"].includes(status);
                            return (
                                <li
                                    key={e.event_id}
                                    className="px-5 py-3.5"
                                    data-testid={`admin-stripe-row-${e.event_id}`}
                                >
                                    <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[13.5px] text-zinc-100 truncate">
                                                {e.business_name || e.email || "—"}
                                            </p>
                                            <p className="mt-0.5 text-[12px] text-zinc-500 truncate font-mono">
                                                {e.event_id}
                                            </p>
                                            <p className="mt-0.5 text-[12px] text-zinc-500">
                                                {fmt(e.received_at)}
                                                {e.amount_total ? (
                                                    <>
                                                        <span className="text-zinc-700 mx-1.5">·</span>
                                                        ${(e.amount_total / 100).toFixed(2)}
                                                    </>
                                                ) : null}
                                            </p>
                                            {e.error && (
                                                <p className="mt-1 text-[12px] text-red-300/80 truncate" title={e.error}>
                                                    {e.error}
                                                </p>
                                            )}
                                            {/* Email diagnostic — only render for provisioned rows */}
                                            {status === "provisioned" && (
                                                <p
                                                    className="mt-1 text-[11.5px] truncate"
                                                    data-testid={`admin-stripe-email-status-${e.event_id}`}
                                                    title={e.welcome_email_error || ""}
                                                >
                                                    {e.welcome_email_ok && e.welcome_email_id && (
                                                        <span className="text-emerald-300/90">
                                                            ✓ Welcome email sent · {e.welcome_email_id}
                                                        </span>
                                                    )}
                                                    {!e.welcome_email_ok && e.welcome_email_skipped && (
                                                        <span className="text-amber-300/90">
                                                            ⚠ Welcome email skipped — Resend not configured (RESEND_API_KEY / RESEND_FROM_EMAIL missing)
                                                        </span>
                                                    )}
                                                    {!e.welcome_email_ok && !e.welcome_email_skipped && e.welcome_email_error && (
                                                        <span className="text-red-300/90">
                                                            ✗ Welcome email failed — {e.welcome_email_error}
                                                        </span>
                                                    )}
                                                    {!e.welcome_email_ok && e.welcome_email_ok !== false && (
                                                        <span className="text-zinc-500">
                                                            (legacy row — re-run for email diagnostics)
                                                        </span>
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span
                                                className={[
                                                    "inline-flex items-center gap-1.5 h-6 px-2 rounded-full border text-[11px]",
                                                    styleCls,
                                                ].join(" ")}
                                                data-testid={`admin-stripe-status-${e.event_id}`}
                                            >
                                                {isLegacy ? "legacy" : status.replace(/_/g, " ")}
                                            </span>
                                            {canReplay && (
                                                <button
                                                    type="button"
                                                    onClick={() => replay(e.event_id)}
                                                    disabled={replayingId === e.event_id}
                                                    data-testid={`admin-stripe-replay-${e.event_id}`}
                                                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-zinc-800 bg-zinc-950 text-[12px] text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 transition-colors disabled:opacity-50"
                                                >
                                                    {replayingId === e.event_id ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <RefreshCw className="h-3 w-3" />
                                                    )}
                                                    Replay
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}

